import dayjs from "dayjs";
import { useRecoilValue } from "recoil";
import { ColDef, GridApi } from "ag-grid-community";
import { FormEvent, useState } from "react";
import { ReactComponent as IconDelete } from "../../styles/assets/svg/icon_edit_delete.svg";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { request } from "../../common/api";
import { TechDoc } from "../../common/types/techdoc";
import { GET_TECH_DOC } from "../../common/query-keys";
import { BaseResponse } from "../../common/types/baseRespones";
import { fileDownload } from "../../common/functions/download";
import { currentShipState } from "../../common/states/ship";
import useTechDocSearchInputData from "../../common/hooks/useTechDocSearchInputData";
import { Grid } from "../../components/molecules/Grid";
import { Buttons } from "../../components/atoms/Buttons";
import { SearchBar } from "../../components/molecules/SearchBar";
import { InputDate } from "../../components/atoms/Inputs/InputDate";
import { InputSelect } from "../../components/atoms/Inputs/InputSelect";
import { confirmDialog } from "../../components/containers/ConfirmDialog";
import { PageContent } from "../../components/organisms/Contents/PageContent";
import { TechDocUploadModal } from "../../components/containers/TechDocUploads/TechDocUploadsModal";
import { showAlert } from "../../components/containers/Alert";

export interface TechDocSearchInputs {
  dcmTypeName?: string;
  shiptypeNo?: string;
  shiptypeName?: string;
  shipNo?: string;
  shipName?: string;
  mcyName?: string;
  ssrMachine?: string;
  startDate?: string;
  endDate?: string;
}

export const TechDocPage = () => {
  const queryClient = useQueryClient();
  const currentShip = useRecoilValue(currentShipState);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [modalShow, setModalShow] = useState<boolean>(false);
  const searchDefaultValues = {
    dcmTypeName: "",
    shiptypeNo: currentShip.shipTypeNo,
    shiptypeName: currentShip.shipTypeName,
    shipNo: currentShip.shipNo,
    shipName: currentShip.shipName,
    mcyName: "",
    startDate: dayjs().subtract(1, "month").format("YYYY-MM-DD"),
    endDate: dayjs().format("YYYY-MM-DD"),
  };
  const [searchInputs, setSearchInputs] =
    useState<TechDocSearchInputs>(searchDefaultValues);
  const {
    search,
    documentTypeSelectOptions,
    machinerySelectOptions,
    handleDocumentTypeSelectChange,
    handleMcySelectChange,
    handleDateChange,
  } = useTechDocSearchInputData(searchDefaultValues);

  // 기술 문서 데이터 조회
  const { data: techDocData, isFetching: techDocIsLoading } = useQuery({
    queryKey: [GET_TECH_DOC, searchInputs],
    queryFn: async () => {
      return await request<TechDoc[]>({
        method: "GET",
        url: "/tech-doc",
        params: searchInputs,
      });
    },
  });

  // 데이터 삭제
  const { mutateAsync: deleteFile } = useMutation({
    mutationFn: (dcmId: number) => {
      return request<BaseResponse>({
        method: "DELETE",
        url: `/tech-doc/${dcmId}`,
      });
    },
    onSuccess: (res: BaseResponse) => {
      queryClient.invalidateQueries({ queryKey: [GET_TECH_DOC] });
    },
  });

  /** 그리드 삭제 버튼 */
  const deleteButtonRender = (e: any) => {
    return (
      <Buttons
        type="button"
        size="sm"
        layout="icon"
        icon={<IconDelete />}
        onClick={async () => {
          const isConfirm = await confirmDialog({
            text: "선택한 항목을 삭제하시겠습니까?",
          });

          if (isConfirm) {
            deleteFile(e.data.dcmId);
          }
        }}
      />
    );
  };

  // 파일 다운로드
  const dcmFileDownloads = async (fileName: string, originalName: string) => {
    try {
      await request<Blob>({
        method: "POST",
        url: `/tech-doc/files/${fileName}`,
        responseType: "blob",
      }).then((res: any) => {
        if (res.length !== 0) {
          fileDownload(res, originalName);
        }
      });
    } catch (e: any) {
      console.error(e);
    }
  };

  // 선택된 파일 다운로드
  const downloadSelectedFiles = async () => {
    if (!gridApi) return;

    const selectedRows = gridApi.getSelectedRows();
    if (selectedRows.length === 0) {
      return showAlert("파일을 선택해주세요.");
    }

    for (const row of selectedRows) {
      await dcmFileDownloads(row.dcmFileName, row.dcmOriginalName);
    }
  };

  const onSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSearchInputs(search);
  };

  /** 그리드 열 정의 */
  const columnDefs: ColDef[] = [
    {
      checkboxSelection: true,
      headerCheckboxSelection: true,
      width: 50,
    },
    {
      headerName: "No",
      valueGetter: "node.rowIndex + 1",
      width: 50,
    },
    {
      headerName: "문서유형",
      field: "dcmTypeName",
      width: 160,
    },
    {
      headerName: "장비명",
      field: "dcmMcyName",
      valueGetter: (params: any) =>
        !params.data.dcmMcyName ? "-" : params.data.dcmMcyName,
      width: 100,
    },
    {
      headerName: "문서 제목",
      field: "dcmTitle",
      valueGetter: (params: any) =>
        !params.data.dcmTitle ? "-" : params.data.dcmTitle,
      flex: 1,
    },
    {
      headerName: "등록일",
      field: "userTime",
      valueGetter: (params: any) =>
        dayjs(params.data.userTime).format("YYYY.MM.DD"),
      width: 100,
    },
    {
      headerName: "등록자",
      field: "userName",
      width: 100,
    },
    {
      headerName: "삭제",

      cellRenderer: deleteButtonRender,
    },
  ];

  return (
    <>
      <PageContent depth01Title="정비 지원" depth02Title="기술문서 관리">
        <>
          <SearchBar
            onSearch={onSearch}
            searchInput={
              <>
                <InputSelect
                  name="dcmTypeName"
                  size="md"
                  placeholder="문서 선택"
                  width={100}
                  options={documentTypeSelectOptions}
                  onChange={handleDocumentTypeSelectChange}
                  value={search.dcmTypeName}
                />
                <InputSelect
                  name="dcmMcyName"
                  size="md"
                  placeholder="장비 선택"
                  width={120}
                  options={machinerySelectOptions}
                  onChange={handleMcySelectChange}
                  value={search.mcyName}
                />
                <InputDate
                  size="md"
                  startName="startDate"
                  width={120}
                  defaultValue={search?.startDate}
                  onChange={handleDateChange}
                />
                ~
                <InputDate
                  size="md"
                  startName="endDate"
                  width={120}
                  defaultValue={search?.endDate}
                  onChange={handleDateChange}
                />
              </>
            }
            gridButtons={
              <>
                <Buttons
                  type="button"
                  size="md"
                  layout="primary"
                  label="파일 등록"
                  onClick={() => setModalShow(!modalShow)}
                />
                <Buttons
                  type="button"
                  size="md"
                  layout="primary"
                  label="선택 다운로드"
                  onClick={downloadSelectedFiles}
                />
              </>
            }
          />
          <Grid
            rowData={techDocData}
            columnDefs={columnDefs}
            rowSelection={`multiple`}
            onGridReady={(params) => {
              setGridApi(params.api);
            }}
            loading={techDocIsLoading}
          />
        </>
      </PageContent>
      {modalShow && (
        <TechDocUploadModal
          setModalShow={() => setModalShow(false)}
          formId="create_techdoc"
          currentShipInfo={currentShip}
        />
      )}
    </>
  );
};
